import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import wsService from '../services/websocket';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Clock,
  User,
  DollarSign,
  CheckCircle,
  Send,
  AlertCircle,
  RefreshCw,
  Timer,
  Zap,
  FileText,
  ChevronRight,
  Bell,
  X,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { formatET } from '../utils/timeUtils';
import ReassignConciergeModal from './ReassignConciergeModal';
import UnlinkedCardSubmissionsCard from './UnlinkedCardSubmissionsCard';

// Application status badges
const STATUS_BADGES = {
  new: { color: 'bg-blue-100 text-blue-800', label: 'New' },
  claimed: { color: 'bg-amber-100 text-amber-800', label: 'Claimed' },
  in_review: { color: 'bg-purple-100 text-purple-800', label: 'Processing' },
  submitted_to_lender: { color: 'bg-indigo-100 text-indigo-800', label: 'Submitted' },
  approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
  denied: { color: 'bg-red-100 text-red-800', label: 'Denied' },
  deal_created: { color: 'bg-emerald-100 text-emerald-800', label: 'Deal Created' },
};

// Deal stage badges
const DEAL_STAGE_BADGES = {
  financing_pending: { color: 'bg-amber-100 text-amber-800', label: 'Financing Pending' },
  financing_approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
  financing_denied: { color: 'bg-red-100 text-red-800', label: 'Denied' },
  funded: { color: 'bg-emerald-100 text-emerald-800', label: 'Funded' },
  closed: { color: 'bg-slate-100 text-slate-800', label: 'Closed' },
  cancelled: { color: 'bg-slate-100 text-slate-800', label: 'Cancelled' },
};

// Funding status badges
const FUNDING_STATUS_BADGES = {
  not_submitted: { color: 'bg-slate-100 text-slate-800', label: 'Not Submitted' },
  submitted: { color: 'bg-blue-100 text-blue-800', label: 'Submitted' },
  under_review: { color: 'bg-amber-100 text-amber-800', label: 'Under Review' },
  approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
  funded: { color: 'bg-emerald-100 text-emerald-800', label: 'Funded' },
  denied: { color: 'bg-red-100 text-red-800', label: 'Denied' },
};

const WorkflowDashboard = () => {
  const { user, isPtcUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Workflow data
  const [newApplications, setNewApplications] = useState([]);
  const [inReview, setInReview] = useState([]);
  const [activeDeals, setActiveDeals] = useState([]);
  const [summary, setSummary] = useState({ new_count: 0, in_review_count: 0, active_deals_count: 0 });
  
  // v3.15-972: Missed applications state/fetch/badge REMOVED per spec
  // (block fully deleted from DOM). Reassign dialog also removed below.

  // v3.15-906: per-user dismissed-card set, persisted across reloads via localStorage.
  // Hides cards from THIS user's dashboard only — does NOT delete the underlying
  // application/deal record. Each column tracks its own set so unrelated dismissals
  // never collide.
  const DISMISS_LS_KEY = 'ptc:workflow-dashboard:dismissed';
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(DISMISS_LS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return {
        new:        new Set(Array.isArray(parsed.new)        ? parsed.new        : []),
        in_review:  new Set(Array.isArray(parsed.in_review)  ? parsed.in_review  : []),
        deal:       new Set(Array.isArray(parsed.deal)       ? parsed.deal       : []),
      };
    } catch { return { new: new Set(), in_review: new Set(), deal: new Set() }; }
  });
  const persistDismissed = (next) => {
    try {
      localStorage.setItem(DISMISS_LS_KEY, JSON.stringify({
        new:       Array.from(next.new),
        in_review: Array.from(next.in_review),
        deal:      Array.from(next.deal),
      }));
    } catch { /* ignore */ }
  };
  const dismissCard = (column, id) => {
    setDismissed((prev) => {
      const nextSet = new Set(prev[column]);
      nextSet.add(id);
      const next = { ...prev, [column]: nextSet };
      persistDismissed(next);
      return next;
    });
  };

  // v3.15-958-WORKFLOW-DISMISS: actually persist the dismissal so the
  // card stops re-appearing on every fetch (the previous local-only set
  // got blown away on refresh and across users). Server-side filter
  // hides the row in /v2/dashboard/workflow. UNDO restores instantly.
  // Optimistic UI: the row leaves the column the moment the X is clicked.
  const dismissingRef = useRef(new Set());
  const dismissCardServer = async (column, id) => {
    if (!id || dismissingRef.current.has(id)) return;
    dismissingRef.current.add(id);
    // Optimistic — fade-out is the natural reaction once the local set
    // includes it (the list filters with `dismissed[column].has(id)`).
    dismissCard(column, id);
    try {
      await api.post(`/v2/applications/${id}/dismiss`);
      toast.success('Application removed from view', {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await api.post(`/v2/applications/${id}/restore`);
              setDismissed((prev) => {
                const nextSet = new Set(prev[column]);
                nextSet.delete(id);
                const next = { ...prev, [column]: nextSet };
                persistDismissed(next);
                return next;
              });
              await fetchWorkflowData();
            } catch (e) {
              toast.error('Could not restore — try again from the deals list');
            }
          },
        },
      });
    } catch (e) {
      // Roll back optimistic update on failure.
      setDismissed((prev) => {
        const nextSet = new Set(prev[column]);
        nextSet.delete(id);
        const next = { ...prev, [column]: nextSet };
        persistDismissed(next);
        return next;
      });
      toast.error(e?.response?.data?.detail || 'Failed to dismiss');
    } finally {
      // Allow another dismiss attempt after the in-flight one settles.
      setTimeout(() => dismissingRef.current.delete(id), 400);
    }
  };
  const restoreColumn = (column) => {
    setDismissed((prev) => {
      const next = { ...prev, [column]: new Set() };
      persistDismissed(next);
      return next;
    });
  };
  
  // Real-time notification banner
  const [notificationBanner, setNotificationBanner] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const timerRef = useRef(null);
  
  // Confirmation dialogs
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  // v3.15-972: reassignDialogOpen removed (Missed Applications panel deleted).
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // v3.15-934: admin-only "Reassign Concierge" modal state.
  const [reassignConciergeOpen, setReassignConciergeOpen] = useState(false);
  const [reassignConciergeApp,  setReassignConciergeApp]  = useState(null);

  const isAdmin = () => {
    const adminRoles = ['ptc_admin', 'ptc_director'];
    return adminRoles.includes(user?.role);
  };

  // Check if user is a manager/director (can see missed applications)
  const isManager = () => {
    const managerRoles = ['company_manager', 'company_director', 'ptc_admin', 'ptc_director', 'ptc_manager'];
    return managerRoles.includes(user?.role);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Fetch workflow data
  const fetchWorkflowData = useCallback(async () => {
    try {
      const response = await api.get('/v2/dashboard/workflow');
      setNewApplications(response.data.new_applications || []);
      setInReview(response.data.in_review || []);
      setActiveDeals(response.data.active_deals || []);
      setSummary(response.data.summary || { new_count: 0, in_review_count: 0, active_deals_count: 0 });
      // v3.15-972: missed-applications fetch removed (block deleted).
    } catch (error) {
      console.error('Failed to fetch workflow data:', error);
      toast.error('Failed to load workflow data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.role]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWorkflowData();
    toast.success('Workflow refreshed');
  };

  // Calculate remaining time for timer
  const calculateRemainingSeconds = (expiresAt) => {
    if (!expiresAt) return 0;
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffMs = expires - now;
    return Math.max(0, Math.floor(diffMs / 1000));
  };

  // Format timer display
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle claim application
  const handleClaim = async () => {
    if (!selectedApplication) return;
    
    setActionLoading(true);
    try {
      await api.post(`/v2/applications/${selectedApplication.id}/claim`);
      toast.success('Application claimed successfully!');
      setClaimDialogOpen(false);
      setSelectedApplication(null);
      setNotificationBanner(null);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      await fetchWorkflowData();
    } catch (error) {
      console.error('Failed to claim application:', error);
      toast.error(error.response?.data?.detail || 'Failed to claim application');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle submit to lender
  const handleSubmitToLender = async () => {
    if (!selectedApplication) return;
    
    setActionLoading(true);
    try {
      const response = await api.post(`/v2/applications/${selectedApplication.id}/submit-to-lender`);
      toast.success(`Deal ${response.data.deal_number} created successfully!`);
      setSubmitDialogOpen(false);
      setSelectedApplication(null);
      await fetchWorkflowData();
    } catch (error) {
      console.error('Failed to submit to lender:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit to lender');
    } finally {
      setActionLoading(false);
    }
  };

  // v3.15-972: handleReassign / openReassignDialog removed alongside the
  // Missed Applications panel — they had no other callers.

  // Open claim dialog
  const openClaimDialog = (app) => {
    setSelectedApplication(app);
    setClaimDialogOpen(true);
  };

  // Open submit dialog
  const openSubmitDialog = (app) => {
    setSelectedApplication(app);
    setSubmitDialogOpen(true);
  };

  // v3.15-909: Auto-assign on click. If the application is unclaimed, claim it
  // for the current user (no modal, instant move to Processing) before navigating.
  // v3.15-933: First-touch ownership = Application CONCIERGE only. Sales rep
  // / PTC rep / manager fields are NEVER modified by this click.
  const autoAssignAndNavigate = async (app) => {
    if (!app?.client_id) {
      toast.error('Application is missing client_id - cannot navigate');
      return;
    }
    const myId = user?.id || user?.user_id;
    // v3.15-933: claim status comes from application_concierge_id (canonical)
    // with a fallback to legacy ptc_rep_id / sales_rep_id for older claimed
    // cards still in the dashboard.
    const conciergeId   = app.application_concierge_id || app.claimed_by || app.ptc_rep_id || app.sales_rep_id || null;
    const conciergeName = app.application_concierge_name || app.ptc_rep_name || app.sales_rep_name || null;
    const alreadyMine     = conciergeId && myId && conciergeId === myId;
    const alreadyAssigned = !!conciergeId;
    if (!alreadyAssigned) {
      try {
        const res = await api.post(`/v2/applications/${app.id}/assign`, {});
        // Optimistically pull this card out of "new" and drop it into "in_review".
        const moved = {
          ...app,
          application_concierge_id:           res.data?.application_concierge_id   || res.data?.assigned_to_id,
          application_concierge_name:         res.data?.application_concierge_name || res.data?.assigned_to_name,
          application_concierge_assigned_at:  res.data?.application_concierge_assigned_at || res.data?.assigned_at,
          claimed_by:                         res.data?.application_concierge_id   || res.data?.assigned_to_id,
          claimed_at:                         res.data?.application_concierge_assigned_at || res.data?.assigned_at,
          application_status:                 res.data?.status || 'claimed',
          // v3.15-933: DO NOT mirror to ptc_rep_* / sales_rep_* — those are
          // set by the originating-rep flow and must stay untouched.
        };
        setNewApplications((prev) => prev.filter((a) => a.id !== app.id));
        setInReview((prev) => [moved, ...prev]);
        setSummary((prev) => ({
          ...prev,
          new_count: Math.max(0, (prev?.new_count || 0) - 1),
          in_review_count: (prev?.in_review_count || 0) + 1,
        }));
        toast.success(`Concierge Assigned: ${res.data?.application_concierge_name || res.data?.assigned_to_name || 'you'}`);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || 'Failed to claim';
        if (status === 409) {
          toast.warning(detail); // "Already claimed by <Concierge Name>"
        } else if (status !== 403) {
          toast.error(detail);
        }
        // Continue to navigate either way so the user can still see the deal.
      }
    } else if (!alreadyMine) {
      toast.info(`Already claimed by ${conciergeName || 'another concierge'}`);
    }
    goToClientPanel(app.client_id, app.id);
  };

  // Navigate to client panel when clicking a card
  const goToClientPanel = (clientId, applicationId) => {
    if (clientId) {
      // Navigate to client detail page with application context
      navigate(`/clients/${clientId}${applicationId ? `?app=${applicationId}` : ''}`);
    } else {
      toast.error('Client record not found');
    }
  };

  // Initial fetch
  useEffect(() => {
    if (isPtcUser()) {
      fetchWorkflowData();
    }
  }, [isPtcUser, fetchWorkflowData]);

  // WebSocket notifications for new applications
  useEffect(() => {
    if (!isPtcUser()) return;

    const handleNewApplication = (data) => {
      if (data.type === 'new_application') {
        // Show notification banner
        setNotificationBanner({
          clientName: data.client_name,
          loanAmount: data.loan_amount,
          applicationId: data.application_id,
          applicationNumber: data.application_number,
          timerExpiresAt: data.timer_expires_at,
        });
        
        // Start countdown timer
        const remainingSeconds = calculateRemainingSeconds(data.timer_expires_at);
        setTimerSeconds(remainingSeconds);
        
        // Update timer every second
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        timerRef.current = setInterval(() => {
          setTimerSeconds((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              timerRef.current = null;
              setNotificationBanner(null);
              fetchWorkflowData(); // Refresh when timer expires
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Refresh data to show new application
        fetchWorkflowData();
        
        // Play notification sound (optional)
        try {
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.3;
          audio.play().catch((e) => console.warn('[WorkflowDashboard] notification sound failed', e));
        } catch (e) { console.warn('[WorkflowDashboard] Audio ctor failed', e); }
      }
    };

    const unsubscribe = wsService.subscribe('new_application', handleNewApplication);

    return () => {
      unsubscribe();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPtcUser, fetchWorkflowData]);

  // Update timers for applications every second
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update countdown displays
      setNewApplications((prev) => [...prev]);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Dismiss notification banner
  const dismissNotification = () => {
    setNotificationBanner(null);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  if (!isPtcUser()) {
    return null;
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-16 bg-slate-200 rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-96 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="workflow-dashboard">
      {/* v3.15-963: Unlinked Credit Card Submissions — admin/director only. */}
      <UnlinkedCardSubmissionsCard userRole={user?.role} />

      {/* Real-time Notification Banner */}
      {notificationBanner && (
        <div 
          className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4 rounded-xl shadow-lg animate-pulse-slow"
          data-testid="new-application-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-white/20 rounded-full">
                <Bell className="h-6 w-6" />
              </div>
              <div>
                <div className="font-bold text-lg">New Application Assigned</div>
                <div className="text-sm text-white/90">
                  {notificationBanner.clientName} • {formatCurrency(notificationBanner.loanAmount)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-mono font-bold" data-testid="countdown-timer">
                  {formatTimer(timerSeconds)}
                </div>
                <div className="text-xs text-white/80">Claim before timer expires</div>
              </div>
              <Button
                variant="secondary"
                className="bg-white text-amber-600 hover:bg-white/90"
                onClick={() => {
                  const app = newApplications.find(a => a.id === notificationBanner.applicationId);
                  if (app) openClaimDialog(app);
                }}
                data-testid="claim-from-banner-btn"
              >
                <Zap className="h-4 w-4 mr-1" />
                Claim Now
              </Button>
              <button
                onClick={dismissNotification}
                className="p-1 hover:bg-white/20 rounded-full"
                aria-label="Dismiss"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Application Workflow</h2>
            <p className="text-sm text-slate-500">Application → Review → Deal</p>
          </div>
          {/* v3.15-972: Missed Applications badge REMOVED. */}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-workflow-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{summary.new_count}</div>
            <div className="text-sm text-blue-700">New Applications</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{summary.in_review_count}</div>
            <div className="text-sm text-amber-700">Processing</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-emerald-600">{summary.active_deals_count}</div>
            <div className="text-sm text-emerald-700">Active Deals</div>
          </CardContent>
        </Card>
      </div>

      {/* 3-Column Workflow */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Column 1: New Applications */}
        <Card data-testid="new-applications-column">
          <CardHeader className="bg-blue-50 border-b border-blue-200 py-3">
            <CardTitle className="text-base flex items-center gap-2 text-blue-800">
              <Clock className="h-5 w-5" />
              New Applications
              <Badge variant="secondary" className="ml-auto bg-blue-100 text-blue-800">
                {newApplications.filter((a) => !dismissed.new.has(a.id)).length}
              </Badge>
            </CardTitle>
            {dismissed.new.size > 0 && (
              <button
                type="button"
                onClick={() => restoreColumn('new')}
                className="self-start text-[11px] text-blue-700 hover:text-blue-900 underline decoration-dotted underline-offset-2"
                data-testid="restore-dismissed-new"
              >
                Restore {dismissed.new.size} hidden
              </button>
            )}
          </CardHeader>
          <ScrollArea className="h-[500px]">
            <CardContent className="p-3 space-y-3">
              {newApplications.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No new applications</p>
                </div>
              ) : (
                newApplications.filter((app) => !dismissed.new.has(app.id)).map((app) => {
                  const remainingSeconds = calculateRemainingSeconds(app.timer_expires_at);
                  const isUrgent = remainingSeconds < 120;
                  // v3.15-933: claim ownership comes from application_concierge_id
                  // (canonical) with legacy fallbacks for older claimed cards.
                  const conciergeId   = app.application_concierge_id || app.claimed_by || app.ptc_rep_id || app.sales_rep_id || null;
                  const conciergeName = app.application_concierge_name || app.ptc_rep_name || app.sales_rep_name || '';
                  const isAssignedToMe = conciergeId === user?.id;
                  
                  return (
                    <div 
                      key={app.id}
                      className={`relative p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${isUrgent ? 'bg-red-50 border-red-200 animate-pulse' : 'bg-white border-slate-200'} ${isAssignedToMe ? 'ring-2 ring-amber-400' : ''}`}
                      data-testid={`new-app-card-${app.id}`}
                      onClick={() => {
                        autoAssignAndNavigate(app);
                      }}
                    >
                      {/* v3.15-906: per-user dismiss */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); dismissCardServer('new', app.id); }}
                        className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
                        title="Dismiss from my dashboard (does not delete)"
                        data-testid={`dismiss-new-app-${app.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-medium text-slate-800 hover:text-blue-600">{app.client_name || app.full_name}</div>
                          <div className="text-xs text-slate-500">{app.application_number}</div>
                        </div>
                        {app.timer_status === 'active' && (
                          <div className={`text-sm font-mono font-bold ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>
                            <Timer className="h-3 w-3 inline mr-1" />
                            {formatTimer(remainingSeconds)}
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-xs text-slate-600 mb-3">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span className="text-slate-500">Originating Rep:</span>
                          <span>{app.originating_rep || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          <span className="text-slate-500">Amount:</span>
                          <span className="font-medium">{formatCurrency(app.amount_requested)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span className="text-slate-500">Submitted:</span>
                          <span>{app.submission_time ? formatET(app.submission_time, 'MMM d, h:mm a') : 'N/A'}</span>
                        </div>
                      </div>
                      
                      {isAssignedToMe && (
                        <Button
                          size="sm"
                          className="w-full bg-amber-500 hover:bg-amber-600"
                          onClick={(e) => { e.stopPropagation(); openClaimDialog(app); }}
                          data-testid={`claim-btn-${app.id}`}
                        >
                          <Zap className="h-4 w-4 mr-1" />
                          Claim Application
                        </Button>
                      )}
                      {!isAssignedToMe && conciergeName && (
                        <div className="text-xs text-center text-emerald-700 bg-emerald-50 border border-emerald-200 rounded py-1" data-testid={`concierge-assigned-${app.id}`}>
                          Concierge Assigned: {conciergeName}
                        </div>
                      )}
                      {isAssignedToMe && (
                        <div className="text-xs text-center text-emerald-700 bg-emerald-50 border border-emerald-200 rounded py-1 mt-1" data-testid={`concierge-mine-${app.id}`}>
                          Application Concierge: {conciergeName || (user?.full_name || user?.email || 'You')}
                        </div>
                      )}
                      {/* v3.15-934: admin-only reassign concierge */}
                      {isAdmin() && conciergeId && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full mt-1 h-6 text-[10px] border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                          onClick={(e) => { e.stopPropagation(); setReassignConciergeApp(app); setReassignConciergeOpen(true); }}
                          data-testid={`reassign-concierge-btn-${app.id}`}
                        >
                          Re-assign Concierge
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </ScrollArea>
        </Card>

        {/* Column 2: Applications Processing */}
        <Card data-testid="in-review-column">
          <CardHeader className="bg-amber-50 border-b border-amber-200 py-3">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <FileText className="h-5 w-5" />
              Processing
              <Badge variant="secondary" className="ml-auto bg-amber-100 text-amber-800">
                {inReview.filter((a) => !dismissed.in_review.has(a.id)).length}
              </Badge>
            </CardTitle>
            {dismissed.in_review.size > 0 && (
              <button
                type="button"
                onClick={() => restoreColumn('in_review')}
                className="self-start text-[11px] text-amber-700 hover:text-amber-900 underline decoration-dotted underline-offset-2"
                data-testid="restore-dismissed-in_review"
              >
                Restore {dismissed.in_review.size} hidden
              </button>
            )}
          </CardHeader>
          <ScrollArea className="h-[500px]">
            <CardContent className="p-3 space-y-3">
              {inReview.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No applications in review</p>
                </div>
              ) : (
                inReview.filter((app) => !dismissed.in_review.has(app.id)).map((app) => {
                  const statusBadge = STATUS_BADGES[app.application_status] || STATUS_BADGES.claimed;
                  
                  return (
                    <div 
                      key={app.id}
                      className="relative p-3 rounded-lg border bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                      data-testid={`review-app-card-${app.id}`}
                      onClick={() => {
                        if (!app.client_id) {
                          toast.error('Application is missing client_id - cannot navigate');
                          return;
                        }
                        goToClientPanel(app.client_id, app.id);
                      }}
                    >
                      {/* v3.15-906: per-user dismiss */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); dismissCardServer('in_review', app.id); }}
                        className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
                        title="Dismiss from my dashboard (does not delete)"
                        data-testid={`dismiss-review-app-${app.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-medium text-slate-800 hover:text-blue-600">{app.client_name || app.full_name}</div>
                          <div className="text-xs text-slate-500">{app.application_number}</div>
                        </div>
                        <Badge className={statusBadge.color}>{statusBadge.label}</Badge>
                      </div>
                      
                      <div className="space-y-1 text-xs text-slate-600 mb-3">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span className="text-slate-500">Originating Rep:</span>
                          <span>{app.originating_rep || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          <span className="text-slate-500">Amount:</span>
                          <span className="font-medium">{formatCurrency(app.amount_requested)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span className="text-slate-500">Concierge:</span>
                          <span className="font-medium text-emerald-700" data-testid={`processing-concierge-${app.id}`}>
                            {app.application_concierge_name || app.ptc_rep_name || '—'}
                          </span>
                        </div>
                      </div>
                      
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={(e) => { e.stopPropagation(); openSubmitDialog(app); }}
                        data-testid={`submit-btn-${app.id}`}
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Submit to Lender
                      </Button>
                      {/* v3.15-934: admin-only reassign concierge in Processing column */}
                      {isAdmin() && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full mt-1 h-6 text-[10px] border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                          onClick={(e) => { e.stopPropagation(); setReassignConciergeApp(app); setReassignConciergeOpen(true); }}
                          data-testid={`reassign-concierge-processing-btn-${app.id}`}
                        >
                          Re-assign Concierge
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </ScrollArea>
        </Card>

        {/* Column 3: Active Deals */}
        <Card data-testid="active-deals-column">
          <CardHeader className="bg-emerald-50 border-b border-emerald-200 py-3">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
              <CheckCircle className="h-5 w-5" />
              Active Deals
              <Badge variant="secondary" className="ml-auto bg-emerald-100 text-emerald-800">
                {activeDeals.filter((d) => !dismissed.deal.has(d.id)).length}
              </Badge>
            </CardTitle>
            {dismissed.deal.size > 0 && (
              <button
                type="button"
                onClick={() => restoreColumn('deal')}
                className="self-start text-[11px] text-emerald-700 hover:text-emerald-900 underline decoration-dotted underline-offset-2"
                data-testid="restore-dismissed-deal"
              >
                Restore {dismissed.deal.size} hidden
              </button>
            )}
          </CardHeader>
          <ScrollArea className="h-[500px]">
            <CardContent className="p-3 space-y-3">
              {activeDeals.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No active deals</p>
                </div>
              ) : (
                activeDeals.filter((deal) => !dismissed.deal.has(deal.id)).map((deal) => {
                  const stageBadge = DEAL_STAGE_BADGES[deal.deal_stage] || DEAL_STAGE_BADGES.financing_pending;
                  const fundingBadge = FUNDING_STATUS_BADGES[deal.funding_status] || FUNDING_STATUS_BADGES.not_submitted;
                  
                  return (
                    <div 
                      key={deal.id}
                      className="relative p-3 rounded-lg border bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                      data-testid={`deal-card-${deal.id}`}
                      onClick={() => {
                        if (!deal.client_id) {
                          toast.error('Deal is missing client_id - cannot navigate');
                          return;
                        }
                        goToClientPanel(deal.client_id, deal.application_id);
                      }}
                    >
                      {/* v3.15-906: per-user dismiss */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); dismissCard('deal', deal.id); }}
                        className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
                        title="Dismiss from my dashboard (does not delete)"
                        data-testid={`dismiss-deal-${deal.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-medium text-slate-800 hover:text-blue-600">{deal.client_name}</div>
                          <div className="text-xs text-slate-500">{deal.deal_number}</div>
                        </div>
                        <Badge className={stageBadge.color}>{stageBadge.label}</Badge>
                      </div>
                      
                      <div className="space-y-1 text-xs text-slate-600">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span className="text-slate-500">Originating Rep:</span>
                          <span>{deal.originating_rep || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span className="text-slate-500">PTC Rep:</span>
                          <span className="font-medium text-emerald-700">{deal.ptc_rep_name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          <span className="text-slate-500">Approved:</span>
                          <span className="font-medium">
                            {deal.approved_amount ? formatCurrency(deal.approved_amount) : 'Pending'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          <span className="text-slate-500">Funding:</span>
                          <Badge className={`text-xs ${fundingBadge.color}`}>{fundingBadge.label}</Badge>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </ScrollArea>
        </Card>
      </div>

      {/* v3.15-972: MISSED APPLICATIONS PANEL — REMOVED.
          User explicitly requested full deletion (block was cluttering UI). */}

      {/* Claim Confirmation Dialog */}
      <AlertDialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Claim Application?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedApplication && (
                <div className="space-y-2 mt-2">
                  <div><strong>Client:</strong> {selectedApplication.client_name || selectedApplication.full_name}</div>
                  <div><strong>Amount:</strong> {formatCurrency(selectedApplication.amount_requested)}</div>
                  <div><strong>Application:</strong> {selectedApplication.application_number}</div>
                  <p className="text-amber-600 mt-3">
                    Claiming this application will stop the timer and assign you as the responsible PTC rep.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClaim}
              disabled={actionLoading}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-claim-btn"
            >
              {actionLoading ? 'Claiming...' : 'Claim Application'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit to Lender Confirmation Dialog */}
      <AlertDialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit to Lender?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedApplication && (
                <div className="space-y-2 mt-2">
                  <div><strong>Client:</strong> {selectedApplication.client_name || selectedApplication.full_name}</div>
                  <div><strong>Amount:</strong> {formatCurrency(selectedApplication.amount_requested)}</div>
                  <div><strong>Application:</strong> {selectedApplication.application_number}</div>
                  <p className="text-blue-600 mt-3">
                    This will create a DEAL record and submit the application to EasyQuickFund for financing.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmitToLender}
              disabled={actionLoading}
              data-testid="confirm-submit-btn"
            >
              {actionLoading ? 'Submitting...' : 'Submit to Lender'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* v3.15-972: Reassign dialog REMOVED with the Missed Applications panel. */}

      {/* v3.15-934: Admin-only Reassign Concierge modal */}
      <ReassignConciergeModal
        open={reassignConciergeOpen}
        onOpenChange={setReassignConciergeOpen}
        application={reassignConciergeApp}
        onReassigned={({ applicationId, application_concierge_id, application_concierge_name }) => {
          // Patch state in both columns so the new concierge name shows up
          // immediately without a full reload.
          const patcher = (list) => list.map((a) => a.id === applicationId
            ? { ...a,
                application_concierge_id,
                application_concierge_name,
                claimed_by: application_concierge_id,
              }
            : a
          );
          setNewApplications(patcher);
          setInReview(patcher);
        }}
      />
    </div>
  );
};

export default WorkflowDashboard;
